import { FIND_MY_INVOICES, GET_INVOICE } from "../../graphql/invoices";
import { GET_ASSET } from "../../graphql/asset";
import {
    buildAsset,
    buildInvoice,
    buildInvoicePageResult,
    buildUser,
    buildWorkerInfo,
} from "../factories";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing/react";
import React, { useMemo } from "react";
import { Platform } from "react-native";
import ClientInvoicesScreen from "../../screens/client/InvoiceList";
import InvoiceDetailsScreen from "../../screens/client/InvoiceDetails";
import { ProfileType } from "../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import { createApolloCache } from "../../configs/graphql.config";
import { createPlatformStackNavigator } from "../../navigations/createPlatformStackNavigator";
import { PDF_SHEET_CONTENT, PDF_SHEET_PLATFORMS } from "../../navigations/pdf-sheet";
import { StoryStackNavigatorContainer } from "../display-helpers";

const INVOICE_ASSET_ID = "invoice-asset-01";

// A single offset-aware mock answers the initial query, the on-mount fetchMore
// and every "load more" page (variables: () => true matches them all).
const buildMocks = (totalInvoices: number) => [
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: FIND_MY_INVOICES,
            variables: () => true,
        },
        result: (variables: { offset?: number }) => ({
            data: {
                findMyInvoices: buildInvoicePageResult(totalInvoices, variables.offset ?? 0),
            },
        }),
    },
    // Tapping any row opens the detail screen, which fetches the invoice then polls its asset
    // until it is ready. Both answer whatever id was tapped.
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_INVOICE,
            variables: () => true,
        },
        result: (variables: { invoiceId: string }) => ({
            data: {
                getInvoice: buildInvoice({
                    id: variables.invoiceId,
                    invoiceAsset: buildAsset({ id: INVOICE_ASSET_ID }),
                }),
            },
        }),
    },
    {
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_ASSET,
            variables: () => true,
        },
        result: () => ({
            data: {
                getAsset: buildAsset({ id: INVOICE_ASSET_ID }),
            },
        }),
    },
];

const Stack = createPlatformStackNavigator<{
    InvoiceList: undefined;
    InvoiceDetails: { invoiceId: string };
}>();

// Mirrors how the app registers InvoiceDetails, so the story exercises the real presentation:
// an iOS form sheet plus the inset that lets the PDF resolve a height inside it (BAS-2889), and a
// plain modal on Android where a sheet would steal the PDF's scroll gestures (BAS-2890).
// `presentation` is native-only — the JS stack used on web does not know "formSheet".
const usesSheet = Platform.OS !== "web" && (PDF_SHEET_PLATFORMS[Platform.OS] ?? false);
const invoiceDetailsOptions = {
    headerShown: true,
    title: "Facture",
    ...(usesSheet ? { presentation: "formSheet" as const } : {}),
    ...PDF_SHEET_CONTENT,
};

const StoryComponent = ({ totalInvoices }: { totalInvoices: number }) => {
    const user = useMemo(() => buildUser({ workerInfo: buildWorkerInfo() }), []);
    const mocks = useMemo(() => buildMocks(totalInvoices), [totalInvoices]);
    // Fresh cache per story so pages loaded in one story don't leak into another.
    const cache = useMemo(() => createApolloCache(), []);

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Worker}>
                <MockedProvider mocks={mocks} cache={cache}>
                    {/* The navigator is what makes a row tap open the detail sheet; the container
                        gives the screen an explicit viewport height so the ScrollView scrolls
                        internally on web (otherwise the page scrolls, the ScrollView's onScroll
                        never fires, and load-more never triggers). */}
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="InvoiceList">
                            <Stack.Screen
                                name="InvoiceList"
                                component={ClientInvoicesScreen}
                                options={{ headerShown: false }}
                            />
                            <Stack.Screen
                                name="InvoiceDetails"
                                component={InvoiceDetailsScreen}
                                options={invoiceDetailsOptions}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/InvoiceList",
    component: StoryComponent,
    argTypes: {
        totalInvoices: { control: { type: "number" } },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

// Fewer invoices than one page: the whole list fits, no "load more".
export const Default: Story = {
    args: { totalInvoices: 5 },
};

// More than one page: scroll to the bottom to load the next 12 invoices.
export const WithLoadMore: Story = {
    args: { totalInvoices: 30 },
};
